import html2pdf from 'html2pdf.js';

/** html2canvas(구버전)이 파싱 못 하는 CSS Color 4 색(color(srgb …), color-mix 등) 대응 */
const HTML2CANVAS_COLOR_PROPERTIES = [
  'color',
  'background-color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline-color',
  'text-decoration-color',
  'column-rule-color',
  'fill',
  'stroke'
];

/**
 * 브라우저가 직렬화한 색을 canvas가 이해하는 rgb/#hex로 맞춘다.
 * @param {string} raw
 * @param {CanvasRenderingContext2D} ctx
 * @returns {string|null} null이면 인라인 덮어쓰기 생략
 */
function normalizeColorForHtml2Canvas(raw, ctx) {
  if (raw == null) return null;
  const v = String(raw).trim();
  if (v === '' || v === 'inherit') return null;
  if (v === 'none' || v === 'transparent') return v;
  if (/^rgba?\(/i.test(v)) return v;
  if (/^hsla?\(/i.test(v)) return v;
  if (/^#([0-9a-f]{3,8})$/i.test(v)) return v;

  try {
    ctx.fillStyle = '#000000';
    ctx.fillStyle = v;
    const out = ctx.fillStyle;
    return typeof out === 'string' && out.length ? out : '#000000';
  } catch {
    return '#000000';
  }
}

/**
 * html2canvas 클론 트리에 안전한 색만 남기도록 인라인으로 고정한다.
 * @param {HTMLElement} root
 */
function applyHtml2CanvasColorCompat(root) {
  if (!root || root.nodeType !== 1) return;

  const doc = root.ownerDocument || document;
  const win = doc.defaultView || window;
  const canvas = doc.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;

  const walk = (el) => {
    if (!el || el.nodeType !== 1) return;
    const tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE') return;

    try {
      const cs = win.getComputedStyle(el);

      for (const prop of HTML2CANVAS_COLOR_PROPERTIES) {
        let val = cs.getPropertyValue(prop);
        if (!val || !String(val).trim()) continue;
        val = String(val).trim();
        if ((prop === 'fill' || prop === 'stroke') && val === 'none') continue;

        const safe = normalizeColorForHtml2Canvas(val, ctx);
        if (safe != null) el.style.setProperty(prop, safe, 'important');
      }

      const boxShadow = cs.getPropertyValue('box-shadow');
      if (/color\(/i.test(boxShadow) || /color-mix\(/i.test(boxShadow)) {
        el.style.setProperty('box-shadow', 'none', 'important');
      }
      const textShadow = cs.getPropertyValue('text-shadow');
      if (/color\(/i.test(textShadow) || /color-mix\(/i.test(textShadow)) {
        el.style.setProperty('text-shadow', 'none', 'important');
      }
    } catch {
      /* ignore per-node */
    }

    const { children } = el;
    for (let i = 0; i < children.length; i++) walk(children[i]);
  };

  walk(root);
}

/**
 * html2canvas 옵션에 Color 4 호환 onclone을 합친다.
 * @param {Object} html2canvasOpts
 * @returns {Object}
 */
function withHtml2CanvasColorCompatOnClone(html2canvasOpts = {}) {
  const userOnClone = html2canvasOpts.onclone;
  return {
    ...html2canvasOpts,
    onclone(a, b) {
      if (typeof userOnClone === 'function') {
        userOnClone(a, b);
      }
      let root = b;
      if (a && a.nodeType === 1 && !a.documentElement) {
        root = a;
      } else if (a && a.documentElement) {
        root = b || a.body || a.documentElement;
      }
      applyHtml2CanvasColorCompat(root);
    }
  };
}

/**
 * PDF 다운로드 유틸리티 클래스
 * HTML 요소를 PDF로 변환하여 다운로드하는 기능을 제공합니다.
 */
export class PdfDownloader {
  /**
   * HTML 요소를 PDF로 변환하여 다운로드
   * @param {HTMLElement} element - PDF로 변환할 HTML 요소
   * @param {string} filename - 다운로드할 파일명 (확장자 제외)
   * @param {Object} options - PDF 생성 옵션
   */
  static async downloadAsPdf(element, filename = 'report', options = {}) {
    try {
      // 기본 옵션 설정
      const defaultOptions = {
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#fafaf4'
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        }
      };

      // 사용자 옵션과 기본 옵션 병합
      const pdfOptions = {
        ...defaultOptions,
        ...options,
        html2canvas: withHtml2CanvasColorCompatOnClone({
          ...defaultOptions.html2canvas,
          ...(options.html2canvas || {})
        })
      };

      console.log('[PdfDownloader] PDF 생성 시작', { filename, options: pdfOptions });

      // PDF 생성 및 다운로드
      await html2pdf()
        .from(element)
        .set(pdfOptions)
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          const totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            if(i == 1) {continue;}
              pdf.setPage(i);
              pdf.setFontSize(10);
              pdf.setTextColor(100);
              console.log('pdf.internal.pageSize.getHeight()', pdf);
              pdf.text('page ' + (i-1) + ' / ' + (totalPages-1), (pdf.internal.pageSize.getWidth() / 2.2), (pdf.internal.pageSize.getHeight() -5));
          }
      }).
      save();

      console.log('[PdfDownloader] PDF 다운로드 완료', { filename });
      return { success: true, filename: pdfOptions.filename };

    } catch (error) {
      console.error('[PdfDownloader] PDF 생성 실패', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 보고서 다운로드 전용 메서드
   * @param {HTMLElement} element - 보고서 HTML 요소
   * @param {string} reportId - 보고서 ID
   * @param {string} siteName - 발전소명
   * @param {Object} fontOptions - 폰트 옵션
   */
  static async downloadReport(element, reportName, siteName, fontOptions = {}) {
    const filename = `SOLVIT_Report${siteName ? `_${siteName}` : ''}_${reportName}`;
    console.log('fontOptions', fontOptions);
    const preparedElement = this.prepareElementForPdf(element, fontOptions);

    return await this.downloadAsPdf(preparedElement, filename, {
      pagebreak: {
        mode: ['css'],
        avoid: '.report-table'
      },
      html2canvas: {
        scale: Number(fontOptions.scale) || 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        allowTaint: true,
        width: Number(fontOptions.width) || preparedElement.scrollWidth,
        windowWidth: Number(fontOptions.width) || preparedElement.scrollWidth,
        height: fontOptions.height,
        windowHeight: preparedElement.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        letterRendering: true,
        removeContainer: true
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      }
    });
  }

  /**
   * PDF 생성 전 요소 준비
   * @param {HTMLElement} element - 준비할 HTML 요소
   * @param {Object} fontOptions - 폰트 옵션
   */
  static prepareElementForPdf(element, fontOptions = {}) {
    // 기본 폰트 옵션
    const defaultFontOptions = {
      baseFontSize: '12px',
      baseFontWeight: 'normal',
      headerFontSize: '18px',
      headerFontWeight: 'bold',
      subHeaderFontSize: '14px',
      subHeaderFontWeight: 'bold',
      tableFontSize: '11px',
      tableFontWeight: 'normal',
      tableHeaderFontWeight: 'bold',
      lineHeight: '1.4',
      fontFamily: 'Arial, sans-serif'
    };

    const options = { ...defaultFontOptions, ...fontOptions };

    // 요소 복제
    const clonedElement = element.cloneNode(true);
    clonedElement.classList.add('report-pdf-light-mode');
    clonedElement.style.backgroundColor = '#ffffff';
    clonedElement.style.color = '#1a1a1a';
    console.log('calculateElementSize2', options);
    // 버튼 제거 (PDF 다운로드 버튼, X버튼 등)
    const buttons = clonedElement.querySelectorAll('button, .v-btn, .header-buttons');
    buttons?.forEach(button => {
      if (button.parentNode) {
        button.parentNode.removeChild(button);
      }
    });

    const skipPdfBackgroundTags = new Set([
      'IMG', 'SVG', 'CANVAS', 'BR', 'HR', 'COL', 'COLGROUP', 'STYLE', 'SCRIPT'
    ]);

    // PDF용 폰트·스타일 적용: 기본은 화면 색을 최대한 유지하고, 테마 변수만 인쇄 친화적으로 보정
    const allElements = clonedElement.querySelectorAll('*');
    allElements?.forEach(el => {
      const tag = el.tagName;
      if (skipPdfBackgroundTags.has(tag)) {
        return;
      }
      if (typeof el.closest === 'function' && el.closest('svg')) {
        return;
      }
      // 기본 폰트 스타일
      el.style.fontFamily = options.fontFamily;
      el.style.fontSize = options.baseFontSize;
      el.style.fontWeight = options.baseFontWeight;
      el.style.lineHeight = options.lineHeight;
      // 화면 대비를 최대한 보존하고, 극단적인 투명 배경만 흰색으로 보정
      const computedBg = window.getComputedStyle(el).backgroundColor;
      if (computedBg === 'rgba(0, 0, 0, 0)' || computedBg === 'transparent') {
        el.style.backgroundColor = '#ffffff';
      }
    });

    // 헤더 스타일 조정
    const headers = clonedElement.querySelectorAll('h1, h3');
    headers?.forEach(header => {
      if (header.tagName === 'H1') {
        header.style.fontSize = options.headerFontSize;
        header.style.fontWeight = options.headerFontWeight;
        header.style.textAlign = 'center';
      } else if (header.tagName === 'H3') {
        header.style.fontSize = options.subHeaderFontSize;
        header.style.fontWeight = options.subHeaderFontWeight;
        header.style.width = '100%';
        header.style.display = 'block';
      }
    });

    // 테이블 스타일 조정
    const tableCells = clonedElement.querySelectorAll('th, td');
    tableCells?.forEach(cell => {
      // 결함 상세 표는 컴포넌트 단에서 레이아웃/폰트 강조를 정교하게 잡았으므로
      // PDF 클론에서 전역 td/th 스타일을 덮어쓰지 않는다.
      if (typeof cell.closest === 'function' && cell.closest('.defect-detail-table')) {
        return;
      }
      cell.style.fontSize = options.tableFontSize;
      cell.style.padding = '6px';
      cell.style.fontWeight = options.tableFontWeight;
    });

    // 테이블 헤더 강조
    const tableHeaders = clonedElement.querySelectorAll('th');
    tableHeaders?.forEach(header => {
      header.style.fontWeight = options.tableHeaderFontWeight;
      header.style.backgroundColor = '#f0f0f0';
      header.style.color = '#1a1a1a';
    });

    return clonedElement;
  }


}

/**
 * PDF 다운로드 훅 (Vue 컴포넌트용)
 */
export const usePdfDownload = () => {
  const downloadReport = async (element, reportName, siteName = '', fontOptions = {}) => {
    try {
      const result = await PdfDownloader.downloadReport(element, reportName, siteName || '', fontOptions);

      if (result.success) {
        console.log('PDF 다운로드 성공:', result.filename);
      } else {
        console.error('PDF 다운로드 실패:', result.error);
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('PDF 다운로드 중 오류:', error);
      throw error;
    }
  };

  return {
    downloadReport
  };
};
